import React from 'react';

import { GatsbyImage } from 'gatsby-plugin-image';
import { useWindowSize } from 'react-use';
import ell from 'stile-shared/assets/images/case-studies/ell.svg';
import location from 'stile-shared/assets/images/case-studies/location.svg';
import setting from 'stile-shared/assets/images/case-studies/setting.svg';
import students from 'stile-shared/assets/images/case-studies/students.svg';
import { CaseStudyWithImageData } from 'stile-shared/src/utils/case-studies';
import styled from 'styled-components';

import { Section } from '../../layout/Section';
import { Icon } from 'stile-shared/src/components/2024/common/Icon';

import { Link } from '../Link';
import VideoIcon from '../VideoIcon';
import { VideoPlayer } from '../VideoPlayer';
import MoreCaseStudies from './MoreCaseStudies';

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: var(--stile-green-2);
  @media (max-width: 1220px) {
    padding: 0px 30px;
  }
`;

const MainContent = styled.div`
  width: 100%;
  background-color: var(--white);
  padding: 100px 0 80px;

  @media (max-width: 768px) {
    padding: 60px 0 60px;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1160px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  @media (max-width: 1220px) {
    padding: 0 30px;
  }
  @media (max-width: 768px) {
    padding: 0 25px;
  }
`;

const FeatureQuote = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  border-left: 4px solid var(--stile-green-2);
  padding-left: 20px;
  margin-bottom: 80px;
  h5 {
    max-width: 731px;
  }
  .h8,
  .--small {
    color: var(--stile-grey-6);
  }
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const CaseStudyContent = styled.div`
  display: flex;
  max-width: 764px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  mark {
    margin: 0 -0.4em;
    padding: 0.1em 0.4em;
    border-radius: 0.8em 0.3em;
    background: transparent;
    background-image: linear-gradient(
      to right,
      rgba(255, 225, 0, 0.1),
      rgba(0, 230, 90, 0.3) 4%,
      rgba(0, 230, 90, 0.1)
    );
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }
`;

const RelatedCaseStudies = styled.div`
  background-color: var(--stile-grey-1);
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const HeaderSection = styled(Section)`
  display: inherit;
  grid-template-columns: repeat(12, 1fr);
  gap: 24px;
  background-color: transparent !important;
  margin: 0 0 !important;
  .content {
    width: 100%;
    margin: 0 0 !important;
  }
  @media (max-width: 768px) {
    width: 100%;
    grid-column: 1 / -1;
    row-gap: 16px;
  }
`;

const HeaderLeft = styled.div`
  grid-column: span 6;
  gap: 20px;
  padding-bottom: 100px;
  .school {
    color: var(--white);
    margin-bottom: 12px;
    max-width: 466px;
  }

  @media (max-width: 768px) {
    grid-column: span 12;
    padding-bottom: 0;
    gap: 15px;
    .school {
      margin-top: 16px;
    }
  }
  .description {
    max-width: 466px;
  }
  .stats {
    width: 90%;
    padding-top: 40px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 32px 32px;
    @media (max-width: 768px) {
      width: 100%;
      padding-top: 28px;
      grid-template-columns: repeat(1, 1fr);
      grid-template-rows: repeat(4, 1fr);
    }
    .stat {
      display: flex;
      gap: 13px;
      .text {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
    }
  }
`;

const PageHeading = styled.h2`
  grid-column: span 12;
  text-align: left !important;
  left: 0;
  margin-bottom: 28px !important;
  @media (max-width: 768px) {
    grid-column: span 12;
    text-align: left !important;
    width: 100%;
  }
`;

const HeaderRight = styled.div`
  grid-column: span 6; /* Desktop: occupies 6/12 columns */
  display: flex;
  justify-content: right;
  align-items: flex-start;

  @media (max-width: 768px) {
    grid-column: span 12;
    padding: 32px 0 60px;
  }
`;

const BreadCrumbs = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  margin-bottom: 32px;
  margin-top: 40px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .separator {
    margin: 0 6px;
    color: var(--stile-grey-7);
  }

  a {
    color: var(--stile-grey-8);
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: var(--stile-green-3);
    }
  }
  .current {
    color: var(--stile-grey-7);
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    max-width: 100%;
  }
  @media (max-width: 768px) {
    margin-top: 17px;
    margin-bottom: 0px;
  }
`;
const StyledImageWrapper = styled.div`
  display: flex;
  min-width: 267px;
  width: 100%;
  justify-content: center;
  .greyBorder {
    border-color: var(--stile-grey-1);
    border-style: solid;
    border-width: 1px;
  }
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.2);
  }

  .gatsby-image-wrapper,
  .overlay {
    border-radius: 32px;
  }
  .cover,
  .withShadow,
  .greyBorder,
  .overlay {
    box-shadow: 0px 41.958px 25.37px 0px rgba(0, 0, 0, 0.04),
      0px 18.54px 18.54px 0px rgba(0, 0, 0, 0.06), 0px 4.879px 10.733px 0px rgba(0, 0, 0, 0.07);
  }
  @media (max-width: 768px) {
    .gatsby-image-wrapper,
    .overlay {
      border-radius: 20px !important;
    }
    .cover {
      box-shadow: none;
    }
  }
`;
interface CaseStudyDetailComponentProps {
  caseStudy: CaseStudyWithImageData;
  allCaseStudies: CaseStudyWithImageData[];
}

export const CaseStudyDetailComponent: React.FC<CaseStudyDetailComponentProps> = ({
  caseStudy,
  allCaseStudies,
}) => {
  return (
    <>
      <Content>
        <div className="content" style={{ width: '100%' }}>
          <BreadCrumbs>
            <Link url={`/why-choose-stile/case-studies#${caseStudy.slug}`}>Case Studies</Link>
            <span className="separator"> &gt; </span>
            <Link className="current" url={`/why-choose-stile/case-studies/${caseStudy.slug}`}>
              {caseStudy.title}
            </Link>
          </BreadCrumbs>
        </div>
        <HeaderSection>
          <HeaderLeft>
            <PageHeading>{caseStudy.title}</PageHeading>
            <h6 className="school">{caseStudy.school}</h6>
            <p className="description">{caseStudy.description}</p>
            <div className="stats">
              <div className="stat">
                <Icon src={location} iconSize="38px" />
                <div className="text">
                  <h6 className="h8">Location</h6>
                  <div className="--small">
                    {caseStudy.location}, {caseStudy.state}
                  </div>
                </div>
              </div>
              <div>
                <div className="stat">
                  <Icon src={students} iconSize="38px" />
                  <div className="text">
                    <h6 className="h8">Students</h6>
                    <div className="--small">{caseStudy.students}</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="stat">
                  <Icon src={setting} iconSize="38px" />
                  <div className="text">
                    <h6 className="h8">Setting</h6>
                    <div className="--small">{caseStudy.setting}</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="stat">
                  <Icon src={ell} iconSize="38px" />
                  <div className="text">
                    <h6 className="h8">English Language Learners</h6>
                    <div className="--small">{caseStudy.englishLanguageLearners}</div>
                  </div>
                </div>
              </div>
            </div>
          </HeaderLeft>
          <HeaderRight>
            <StyledImageWrapper>
              {caseStudy.youtube ? (
                <VideoIcon
                  style="secondary"
                  image={
                    <>
                      <GatsbyImage image={caseStudy.imageData} alt="video thumbnail" />
                      <div className="overlay" />
                    </>
                  }
                  youtubeUrl={caseStudy.youtube}
                />
              ) : caseStudy.video ? (
                <VideoPlayer url={caseStudy.video} aspectRatio="1132/712" loop={true} />
              ) : (
                <GatsbyImage image={caseStudy.imageData} alt={caseStudy.title} />
              )}
            </StyledImageWrapper>
          </HeaderRight>
        </HeaderSection>
      </Content>

      <MainContent>
        <ContentContainer>
          {caseStudy.featureQuote && (
            <FeatureQuote>
              <h5>&quot;{caseStudy.featureQuote.quote}&quot;</h5>
              <div>
                <h6 className="h8">{caseStudy.featureQuote.author}</h6>
                <div className="--small">{caseStudy.featureQuote.position}</div>
              </div>
            </FeatureQuote>
          )}

          <CaseStudyContent dangerouslySetInnerHTML={{ __html: caseStudy.fullCaseStudy }} />
        </ContentContainer>
      </MainContent>

      <RelatedCaseStudies>
        <MoreCaseStudies
          currentCaseStudyId={caseStudy.id}
          caseStudies={allCaseStudies}
          title="More classroom stories"
        />
      </RelatedCaseStudies>
    </>
  );
};

export default CaseStudyDetailComponent;
